import {
  INVALID_SUP_ORG_TEAM_NAME,
  isInvalidSupOrgTeamId,
} from '@internal/plugin-watchtower-common';
import { useEffect, useState, useMemo } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export const getSubStrForLength = (count: number) =>
  count > 1 ? 'resources need' : 'resource needs';

export const getBadgeColor = (
  badge: string,
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  switch (badge) {
    case 'EXCELLENT':
      return 'success';
    case 'CAUTION':
      return 'warning';
    case 'CRITICAL':
      return 'error';
    case 'N/A':
      return 'default';
    default:
      return 'info';
  }
};

export const getChildTeamScoreColor = (
  badge: string,
): 'primary' | 'textSecondary' | 'error' => {
  switch (badge) {
    case 'EXCELLENT':
      return 'primary';
    case 'CAUTION':
      return 'textSecondary';
    case 'CRITICAL':
      return 'error';
    default:
      return 'textSecondary';
  }
};

export const normalizeTeamId = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/^group:default\//, '')
    .trim();
};

export const useTeamNames = (teamIds: string[]) => {
  const catalogApi = useApi(catalogApiRef);
  const [names, setNames] = useState<Record<string, string>>({});

  const uniqueTeamIds = useMemo(
    () => Array.from(new Set(teamIds.filter(Boolean))),
    [teamIds],
  );

  useEffect(() => {
    let cancelled = false;

    if (!uniqueTeamIds.length) {
      return () => {
        cancelled = true;
      };
    }

    const fetchAll = async () => {
      const result: Record<string, string> = {};

      await Promise.all(
        uniqueTeamIds.map(async id => {
          // SUP_INVALID has no catalog entity
          if (isInvalidSupOrgTeamId(id)) {
            result[id] = INVALID_SUP_ORG_TEAM_NAME;
            return;
          }
          try {
            const team = await catalogApi.getEntityByRef(`group:default/${id}`);
            result[id] = team?.metadata?.title ?? id;
          } catch {
            result[id] = id;
          }
        }),
      );

      if (!cancelled) {
        setNames(result);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [uniqueTeamIds, catalogApi]);

  return names;
};
